import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Game } from '../types';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useCompetitionsContext } from './CompetitionsContext';
import { queryCache, CACHE_TTL } from '../lib/cache';
import { visibilityManager } from '../lib/visibilityManager';

interface GamesContextValue {
  games: Game[];
  loading: boolean;
  error: string | null;
  refreshGames: () => Promise<void>;
  getGameById: (id: string) => Game | undefined;
  getGamesByGameweek: (gameweek: number) => Game[];
}

const GamesContext = createContext<GamesContextValue | undefined>(undefined);

export const useGamesContext = () => {
  const context = useContext(GamesContext);
  if (!context) {
    throw new Error('useGamesContext must be used within GamesProvider');
  }
  return context;
};

interface GamesProviderProps {
  children: React.ReactNode;
}

export const GamesProvider: React.FC<GamesProviderProps> = ({ children }) => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentOrganization, activeCompetitionId } = useAuth();
  const competitionsContext = useCompetitionsContext();
  const { competitions } = competitionsContext;
  const [lastCompetitionId, setLastCompetitionId] = React.useState<string | null>(null);

  const fetchGames = useCallback(async () => {
    console.log('[GamesContext] fetchGames called', {
      hasOrg: !!currentOrganization?.id,
      hasCompId: !!activeCompetitionId,
      orgId: currentOrganization?.id,
      compId: activeCompetitionId
    });

    if (!currentOrganization?.id || !activeCompetitionId) {
      console.log('[GamesContext] Missing org or competition ID, setting loading=false');
      setLoading(false);
      return;
    }

    // Wait for competitions to load before fetching games
    const competition = competitionsContext.getCompetitionById(activeCompetitionId);
    if (!competition) {
      console.log('[GamesContext] Competition not loaded yet, waiting...');
      setLoading(true);
      return;
    }

    console.log('[GamesContext] Starting game fetch for competition:', competition.name);

    const cacheKey = queryCache.generateKey('all_games_v3', {
      orgId: currentOrganization.id,
      competitionId: activeCompetitionId
    });

    // Force invalidate cache to ensure fresh data
    queryCache.invalidate(cacheKey);

    try {
      setLoading(true);
      setError(null);

      console.log('[GamesContext] Fetching with timeout protection');

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout after 30s')), 30000);
      });

      const result = await Promise.race([
        queryCache.deduplicate(
          cacheKey,
          async () => {
          // Get competition date range from CompetitionsContext (no extra query needed!)
          const competition = competitionsContext.getCompetitionById(activeCompetitionId);

          if (!competition) {
            console.warn('[GamesContext] Competition disappeared during fetch');
            return [];
          }

          console.log('[GamesContext] Fetching games for competition:', {
            competitionId: activeCompetitionId,
            startDate: competition.startDate.toISOString(),
            endDate: competition.endDate?.toISOString()
          });

          // Fetch games that are linked to this competition via competition_games junction
          const { data: competitionGameIds, error: junctionError } = await supabase
            .from('competition_games')
            .select('game_id')
            .eq('competition_id', activeCompetitionId);

          if (junctionError) {
            console.error('[GamesContext] Error fetching competition_games:', junctionError);
            throw junctionError;
          }

          if (!competitionGameIds || competitionGameIds.length === 0) {
            console.warn('[GamesContext] No games linked to this competition');
            return [];
          }

          const gameIds = competitionGameIds.map(cg => cg.game_id);
          console.log('[GamesContext] Fetching games with IDs:', gameIds.length);

          // Fetch games linked to competition (no date filtering needed since junction table defines scope)
          const { data: gamesData, error: gamesError } = await supabase
            .from('games')
            .select('id, home_team, away_team, home_team_logo, away_team_logo, league, match_date, status, actual_home_score, actual_away_score, gameweek')
            .in('id', gameIds)
            .order('match_date', { ascending: true })
            .limit(10000);

          if (gamesError) {
            console.error('[GamesContext] Error fetching games:', gamesError);
            throw gamesError;
          }

          console.log('[GamesContext] Fetched games count:', gamesData?.length || 0);

          // Log gameweek range for debugging
          const gameweeks = gamesData?.map((g: any) => g.gameweek).filter(Boolean) || [];
          const uniqueGameweeks = [...new Set(gameweeks)].sort((a, b) => a - b);
          console.log('[GamesContext] Gameweek range:', {
            min: Math.min(...uniqueGameweeks),
            max: Math.max(...uniqueGameweeks),
            uniqueGameweeks
          });

          // Log finished games count for debugging
          const finishedCount = gamesData?.filter((g: any) =>
            g.actual_home_score !== null && g.actual_away_score !== null
          ).length || 0;
          console.log('[GamesContext] Finished games count:', finishedCount);

          // Transform raw data to Game format
          const formattedGames: Game[] = gamesData?.map((game: any) => {
            const baseGame = {
              id: game.id,
              homeTeam: game.home_team,
              awayTeam: game.away_team,
              homeTeamLogo: game.home_team_logo || undefined,
              awayTeamLogo: game.away_team_logo || undefined,
              league: game.league,
              matchDate: new Date(game.match_date),
              actualHomeScore: game.actual_home_score === null ? undefined : game.actual_home_score,
              actualAwayScore: game.actual_away_score === null ? undefined : game.actual_away_score
            };

            // Use the database status if available, otherwise determine it
            let determinedStatus: 'upcoming' | 'live' | 'finished' | 'postponed';

            if (game.status && ['upcoming', 'live', 'finished', 'postponed'].includes(game.status.toLowerCase())) {
              // Trust the database status
              determinedStatus = game.status.toLowerCase() as 'upcoming' | 'live' | 'finished' | 'postponed';
            } else {
              // Fallback: determine status from date and scores
              const now = new Date();
              const matchDate = baseGame.matchDate;
              const hasActualScores = baseGame.actualHomeScore !== undefined && baseGame.actualAwayScore !== undefined;

              if (matchDate > now) {
                determinedStatus = 'upcoming';
              } else if (hasActualScores) {
                determinedStatus = 'finished';
              } else {
                determinedStatus = 'live';
              }
            }

            return {
              ...baseGame,
              status: determinedStatus,
              gameweek: game.gameweek
            };
          }) || [];

          return formattedGames;
        },
        CACHE_TTL.DEFAULT
      ),
        timeoutPromise
      ]);

      console.log('[GamesContext] Fetch completed successfully');
      setGames(result);
    } catch (err) {
      console.error('[GamesContext] Error fetching games:', err);
      console.error('[GamesContext] Error type:', typeof err);
      console.error('[GamesContext] Error details:', {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined
      });
      setError(err instanceof Error ? err.message : 'Failed to load games');
      // Keep previously loaded games on transient failures/timeouts.
    } finally {
      console.log('[GamesContext] Setting loading=false');
      setLoading(false);
    }
  }, [currentOrganization?.id, activeCompetitionId, competitionsContext]);

  useEffect(() => {
    // Check if the competition is available
    const competition = activeCompetitionId ? competitionsContext.getCompetitionById(activeCompetitionId) : null;

    console.log('[GamesContext] useEffect triggered:', {
      activeCompetitionId,
      lastCompetitionId,
      competitionsCount: competitions.length,
      gamesCount: games.length,
      hasCompetition: !!competition
    });

    // Only fetch if:
    // 1. Competition ID changed to a different valid ID, OR
    // 2. Competition just became available (was null, now exists) and we have no games
    if (activeCompetitionId && activeCompetitionId !== lastCompetitionId) {
      console.log('[GamesContext] Competition ID changed, clearing games and fetching new data');
      // Clear stale games immediately when competition changes
      setGames([]);
      setLoading(true);
      setLastCompetitionId(activeCompetitionId);
      fetchGames();
    } else if (activeCompetitionId && activeCompetitionId === lastCompetitionId && competition && games.length === 0) {
      // Competition exists but we have no games - retry fetch
      console.log('[GamesContext] Competition loaded, retrying games fetch');
      fetchGames();
    }
    // Don't clear games when activeCompetitionId becomes null during reloads
    // Data will persist until a new competition is selected
  }, [activeCompetitionId, lastCompetitionId, fetchGames, competitionsContext, competitions.length, games.length]);

  useEffect(() => {
    const unsubscribe = visibilityManager.subscribe((isVisible) => {
      if (!isVisible || !activeCompetitionId) {
        return;
      }
      const competition = competitionsContext.getCompetitionById(activeCompetitionId);
      if (!competition) {
        return;
      }
      if (error || games.length === 0) {
        fetchGames();
      }
    });
    return unsubscribe;
  }, [activeCompetitionId, competitionsContext, error, fetchGames, games.length]);

  const getGameById = useCallback((id: string) => {
    return games.find(g => g.id === id);
  }, [games]);

  const getGamesByGameweek = useCallback((gameweek: number) => {
    return games.filter(g => g.gameweek === gameweek);
  }, [games]);

  const value: GamesContextValue = useMemo(() => ({
    games,
    loading,
    error,
    refreshGames: fetchGames,
    getGameById,
    getGamesByGameweek
  }), [games, loading, error, fetchGames, getGameById, getGamesByGameweek]);

  return (
    <GamesContext.Provider value={value}>
      {children}
    </GamesContext.Provider>
  );
};
